import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import './index.css';

function getSentryEnvironment(): string {
  if (typeof window === 'undefined')
    return import.meta.env.MODE;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1')
    return 'development';
  if (
    host.includes('staging--') ||
    host.includes('deploy-preview')
  )
    return 'staging';
  return 'production';
}

const sentryEnv = getSentryEnvironment();
Sentry.init({
  dsn: 'https://8053425fd77003a47d10cd17686f0ce9@o4508546853830656.ingest.us.sentry.io/4510733243383808',
  environment: sentryEnv,
  enabled: sentryEnv !== 'development',
  integrations: [Sentry.browserTracingIntegration()],
  // 100% sampling in staging for E2E debugging, 10% in production
  tracesSampleRate: sentryEnv === 'staging' ? 1.0 : 0.1,
});

// Expose Sentry on window for E2E test synthetic error capture
if (typeof window !== 'undefined') {
  (window as unknown as { Sentry: typeof Sentry }).Sentry =
    Sentry;
}

import { AuthProvider } from './contexts/AuthContext';
import {
  SentryProvider,
  SentryErrorBoundary,
} from './contexts/SentryContext';
import { AudioProvider } from './contexts/AudioContext';
import { unlockAudio } from './lib/audio';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient';
import { ProtectedLayout } from './components/ProtectedLayout';
import { QueryErrorBoundary } from './components/QueryErrorBoundary';
import { UpdateBanner } from './components/UpdateBanner';
// Page components — lazy-loaded so each route becomes its own chunk
const Login = React.lazy(() =>
  import('./pages/Login').then(m => ({ default: m.Login }))
);
const Signup = React.lazy(() =>
  import('./pages/Signup').then(m => ({
    default: m.Signup,
  }))
);
const VerifyEmail = React.lazy(() =>
  import('./pages/VerifyEmail').then(m => ({
    default: m.VerifyEmail,
  }))
);
const ResetPassword = React.lazy(() =>
  import('./pages/ResetPassword').then(m => ({
    default: m.ResetPassword,
  }))
);
const AuthCallback = React.lazy(() =>
  import('./pages/AuthCallback').then(m => ({
    default: m.AuthCallback,
  }))
);
const AuthConfirm = React.lazy(() =>
  import('./pages/AuthConfirm').then(m => ({
    default: m.AuthConfirm,
  }))
);
const LandingRedirect = React.lazy(() =>
  import('./pages/LandingRedirect').then(m => ({
    default: m.LandingRedirect,
  }))
);
const AllRealms = React.lazy(() =>
  import('./pages/AllRealms').then(m => ({
    default: m.AllRealms,
  }))
);
const Realm = React.lazy(() =>
  import('./pages/Realm').then(m => ({ default: m.Realm }))
);
const ManageRealm = React.lazy(() =>
  import('./pages/ManageRealm').then(m => ({
    default: m.ManageRealm,
  }))
);
const RealmInvite = React.lazy(() =>
  import('./pages/RealmInvite').then(m => ({
    default: m.RealmInvite,
  }))
);
const Hero = React.lazy(() =>
  import('./pages/Hero').then(m => ({ default: m.Hero }))
);
const ShoppeLanding = React.lazy(() =>
  import('./pages/Shoppe').then(m => ({
    default: m.ShoppeLanding,
  }))
);
const ShoppePage = React.lazy(() =>
  import('./pages/Shoppe').then(m => ({
    default: m.ShoppePage,
  }))
);
const BodyShoppe = React.lazy(() =>
  import('./pages/Shoppe').then(m => ({
    default: m.BodyShoppe,
  }))
);
const CastRealm = React.lazy(() =>
  import('./pages/Cast').then(m => ({
    default: m.CastRealm,
  }))
);
const StoryQuestPage = React.lazy(() =>
  import('./pages/Realm/StoryQuestPage').then(m => ({
    default: m.StoryQuestPage,
  }))
);
const QuartermasterPage = React.lazy(() =>
  import('./pages/Realm/QuartermasterPage').then(m => ({
    default: m.QuartermasterPage,
  }))
);
const BattleReportPage = React.lazy(() =>
  import('./pages/BattleReport').then(m => ({
    default: m.BattleReportPage,
  }))
);
const PatchNotesPage = React.lazy(() =>
  import('./pages/PatchNotes').then(m => ({
    default: m.PatchNotesPage,
  }))
);
const DevSprites = React.lazy(() =>
  import('./pages/DevSprites').then(m => ({
    default: m.DevSprites,
  }))
);
const DevBattle = React.lazy(() =>
  import('./pages/DevBattle').then(m => ({
    default: m.DevBattle,
  }))
);
const DevShoppe = React.lazy(() =>
  import('./pages/DevShoppe').then(m => ({
    default: m.DevShoppe,
  }))
);
const DevAbilityIcons = React.lazy(() =>
  import('./pages/DevAbilityIcons').then(m => ({
    default: m.DevAbilityIcons,
  }))
);
const DevLayout = React.lazy(() =>
  import('./pages/DevLayout').then(m => ({
    default: m.DevLayout,
  }))
);
const DevItemStudio = React.lazy(() =>
  import('./pages/DevItemStudio').then(m => ({
    default: m.DevItemStudio,
  }))
);
const DevAbilityStudio = React.lazy(() =>
  import('./pages/DevAbilityStudio').then(m => ({
    default: m.DevAbilityStudio,
  }))
);
const DevConditionStudio = React.lazy(() =>
  import('./pages/DevConditionStudio').then(m => ({
    default: m.DevConditionStudio,
  }))
);

// Audio unlock on first user interaction
document.addEventListener('click', unlockAudio, {
  once: true,
});
document.addEventListener('keydown', unlockAudio, {
  once: true,
});

ReactDOM.createRoot(
  document.getElementById('root')!
).render(
  <React.StrictMode>
    <SentryErrorBoundary>
      <UpdateBanner />
      <SentryProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AudioProvider>
              <AuthProvider>
                <QueryErrorBoundary>
                  <Suspense fallback={null}>
                    <Routes>
                      {/* Dev-only routes */}
                      {import.meta.env.DEV && (
                        <Route element={<DevLayout />}>
                          <Route
                            path="/dev/sprites"
                            element={<DevSprites />}
                          />
                          <Route
                            path="/dev/battle"
                            element={<DevBattle />}
                          />
                          <Route
                            path="/dev/shoppe"
                            element={<DevShoppe />}
                          />
                          <Route
                            path="/dev/ability-icons"
                            element={<DevAbilityIcons />}
                          />
                          <Route
                            path="/dev/item-studio"
                            element={<DevItemStudio />}
                          />
                          <Route
                            path="/dev/ability-studio"
                            element={<DevAbilityStudio />}
                          />
                          <Route
                            path="/dev/condition-studio"
                            element={<DevConditionStudio />}
                          />
                        </Route>
                      )}

                      {/* Public routes */}
                      <Route path="/" element={<Login />} />
                      <Route
                        path="/login"
                        element={<Login />}
                      />
                      <Route
                        path="/signup"
                        element={<Signup />}
                      />
                      <Route
                        path="/verify-email"
                        element={<VerifyEmail />}
                      />
                      <Route
                        path="/reset-password"
                        element={<ResetPassword />}
                      />
                      <Route
                        path="/auth/callback"
                        element={<AuthCallback />}
                      />
                      <Route
                        path="/auth/confirm"
                        element={<AuthConfirm />}
                      />
                      <Route
                        path="/invite/:realmId"
                        element={<RealmInvite />}
                      />
                      <Route
                        path="/cast/:code"
                        element={<CastRealm />}
                      />

                      {/* Protected routes */}
                      <Route element={<ProtectedLayout />}>
                        <Route
                          path="/dashboard"
                          element={<LandingRedirect />}
                        />
                        <Route
                          path="/realms"
                          element={<AllRealms />}
                        />
                        <Route
                          path="/realms/:realmId"
                          element={<Realm />}
                        />
                        <Route
                          path="/realms/:realmId/story-quests"
                          element={<StoryQuestPage />}
                        />
                        <Route
                          path="/realms/:realmId/quartermaster"
                          element={<QuartermasterPage />}
                        />
                        <Route
                          path="/realms/:realmId/manage"
                          element={<ManageRealm />}
                        />
                        <Route
                          path="/realms/:realmId/shoppe"
                          element={<ShoppePage />}
                        />
                        <Route
                          path="/realms/:realmId/shoppe/body"
                          element={<BodyShoppe />}
                        />
                        <Route
                          path="/hero"
                          element={<Hero />}
                        />
                        <Route
                          path="/battles/:battleId"
                          element={<BattleReportPage />}
                        />
                        <Route
                          path="/shoppe"
                          element={<ShoppeLanding />}
                        />
                        <Route
                          path="/shoppe/body"
                          element={<BodyShoppe />}
                        />
                        <Route
                          path="/help"
                          element={
                            <Navigate
                              to="https://rurai.dev/products/choreboard/help"
                              replace
                            />
                          }
                        />
                        <Route
                          path="/patch-notes"
                          element={<PatchNotesPage />}
                        />
                      </Route>
                    </Routes>
                  </Suspense>
                </QueryErrorBoundary>
              </AuthProvider>
            </AudioProvider>
          </BrowserRouter>
          {import.meta.env.DEV && (
            <ReactQueryDevtools initialIsOpen={false} />
          )}
        </QueryClientProvider>
      </SentryProvider>
    </SentryErrorBoundary>
  </React.StrictMode>
);
